import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const fpBase = (tenantId) => `/tenants/${tenantId}/fp`;

const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const inrFmt2 = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

export const inr = (value) =>
  value === null || value === undefined || value === "" ? "—" : `₹${inrFmt.format(value)}`;

/** Headline figures read in words — "₹2.48 crore" — so a placard never has to clip. */
export const shortInr = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const a = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (a >= 1e7) return `${sign}₹${(a / 1e7).toFixed(2)} crore`;
  if (a >= 1e5) return `${sign}₹${(a / 1e5).toFixed(2)} lakh`;
  if (a >= 1e3) return `${sign}₹${(a / 1e3).toFixed(1)}k`;
  return `${sign}₹${Math.round(a)}`;
};


export const num2 = (value) =>
  value === null || value === undefined ? "—" : inrFmt2.format(value);

export const pctOf = (value, total) =>
  !total ? "—" : `${((value / total) * 100).toFixed(1)}%`;

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const monthLabel = (period) => {
  if (!period) return "—";
  const [y, m] = String(period).split("-");
  return `${MONTH_NAMES[Number(m) - 1] || m} ${y}`;
};

/** Shared period + monthly/cumulative basis, kept in the URL so every tab stays in step. */
export const useFpContext = () => {
  const { tenantId } = useAuth();
  const [params, setParams] = useSearchParams();
  const [ctx, setCtx] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const { data } = await api.get(`${fpBase(tenantId)}/context`);
      setCtx(data);
    } catch (e) {
      setCtx({ error: e?.response?.data?.detail || "Financial Performance is not available" });
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const period = params.get("period") || ctx?.latest_period || "";
  const basis = params.get("basis") === "ytd" ? "ytd" : "month";
  const set = (next) => {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => merged.set(k, v));
    if (!merged.get("period") && period) merged.set("period", period);
    setParams(merged, { replace: true });
  };

  return { tenantId, ctx, period, basis, setPeriod: (p) => set({ period: p }),
           setBasis: (b) => set({ basis: b }), reloadContext: load };
};

export const RECON_TONE = {
  Reconciled: "bg-success/18 text-success",
  Difference: "bg-destructive/15 text-destructive",
  "Control only": "bg-muted text-muted-foreground",
  "Reconciled (accepted)": "bg-success/12 text-success",
  "No control total": "bg-warning/25 text-[hsl(30_88%_30%)]",
};

export const SEVERITY_TONE = {
  block: "bg-destructive/15 text-destructive",
  warn: "bg-warning/25 text-[hsl(30_88%_30%)]",
  ignore: "bg-muted text-muted-foreground",
};

export const EXCEPTION_STATE_TONE = {
  open: "bg-destructive/12 text-destructive",
  accepted: "bg-success/18 text-success",
  parked: "bg-muted text-muted-foreground",
  suppressed: "bg-muted text-muted-foreground",
};

export const LEVEL_TONE = {
  "Level 3": "bg-success/18 text-success",
  "Level 2": "bg-warning/25 text-[hsl(30_88%_30%)]",
  "Level 1": "bg-destructive/15 text-destructive",
};
